'use client';
import type { IHeaderProfile } from './data';

import React from 'react';
import { Circle, useDisclosure } from '@chakra-ui/react';
import { usePathname, useRouter } from 'next/navigation';

import LogoutConfirmModal from '../LogoutModal';

import {
  Box,
  Button,
  DashboardSideSheet,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  PromotionCodeActivationModal,
  Text,
} from '@/components';
import { usePopups, useProfile, useTranslation } from '@/hooks';
import { routes } from '@/statics';
import AvatarBlack from '@/svgs/User.svg';
import AvatarIcon from '@/svgs/User-1.svg';

const HeaderProfile: IHeaderProfile = () => {
  const router = useRouter();
  const pathName = usePathname();

  const { data: profile } = useProfile();
  const { data: notification } = usePopups({
    profile: Boolean(profile),
    isRead: false,
  });
  const isAuthenticated = Boolean(profile);
  const isAgent = profile?.user_type === 'agent';

  const {
    isOpen: isOpenDashboardSideSheet,
    onOpen: onOpenDashboardSideSheet,
    onClose: onCloseDashboardSideSheet,
  } = useDisclosure();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenPromoCode,
    // onOpen: onOpenPromoCode,
    onClose: onClosePromoCode,
  } = useDisclosure();
  const { t } = useTranslation('components.header');
  type IMenuItem = {
    title: string | React.JSX.Element;
    href?: string;
    hide?: boolean;
    rightElement?: React.ReactElement;
    onClick?: () => void;
  };

  const notLoggedInMenuItems: IMenuItem[] = [
    { title: t('loginToAccount'), href: routes.signIn.url },
    { title: t('customerSignUp'), href: `${routes.signUp.url}?type=customer` },
    { title: t('agentSignUp'), href: `${routes.signUp.url}?type=agent` },
  ];

  const loggedInMenuItems: IMenuItem[] = [
    { title: t('dashboard'), href: routes.dashboard.url },
    // {
    //   title: <Box onClick={() => onOpenPromoCode()}>{t('promotionCode')}</Box>,
    //   href: '',
    // },
    { title: t('purchaseRecords'), href: routes.orders.url },
    { title: t('favorites'), href: routes.favorites.url },
    {
      title: t('becomeAgent'),
      href: routes.identityBecomeAgent.url,
      hide: isAgent,
    },
    { title: t('signOut'), onClick: onOpen },
  ];

  const menuItems = { loggedInMenuItems, notLoggedInMenuItems };

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      if (profile) {
        window.gtag('set', 'user_id', profile?.public_id);
      }
    }
  }, []);

  return (
    <Box position="relative" w={'max-content'} textAlign="left">
      <Box position={'relative'}>
        <Box
          onClick={() => {
            if (profile) {
              onOpenDashboardSideSheet();
              return;
            }
            router.push(`${routes.signIn.url}?returnURL=${pathName}`);
          }}
          position={'relative'}
        >
          <IconButton
            w={'2rem'}
            h={'2rem'}
            borderRadius={'50%'}
            aria-label="Profile icon"
            variant="ghost"
            icon={<AvatarBlack width={'23px'} height={'24px'} />}
            display={{ base: 'inline-flex', md: 'none' }}
          />
          {notification?.list && notification.list.length > 0 && (
            <Circle
              display={{ base: 'flex', md: 'none' }}
              alignItems="center"
              zIndex={1150}
              position="absolute"
              top={'-13px'}
              right="-7px"
              size="27"
              backgroundColor="secondary.500"
              color="black"
              fontWeight="bold"
              fontSize="md"
            >
              <Text marginTop={'4px'}>{notification.list.length}</Text>
            </Circle>
          )}
        </Box>
      </Box>
      <DashboardSideSheet
        onClose={onCloseDashboardSideSheet}
        isOpen={isOpenDashboardSideSheet}
      />
      {isOpenPromoCode && (
        <PromotionCodeActivationModal
          isOpen={isOpenPromoCode}
          onClose={onClosePromoCode}
        />
      )}
      <Box position="relative">
        {notification?.list && notification.list.length > 0 && (
          <Circle
            display={{ base: 'none', md: 'flex' }}
            alignItems="center"
            zIndex={1150}
            position="absolute"
            top="-15px"
            size="30px"
            backgroundColor="secondary.500"
            color="black"
            fontWeight="bold"
            fontSize="md"
          >
            <Text height="max-content" marginTop={'4px'}>
              {notification.list.length}
            </Text>
          </Circle>
        )}
        <Menu strategy="absolute" placement="bottom">
          <MenuButton
            mr={2}
            display={{ base: 'none', md: 'inline-flex' }}
            as={Button}
            size="md"
            rightIcon={<Icon w="18px" h="18px" as={AvatarIcon} />}
            h="46px"
            w="180px"
            zIndex="sticky"
            fontSize="sm"
          >
            {t('account')}
          </MenuButton>
          <MenuList
            w="180px"
            paddingInline="13px"
            pt="50px"
            pb={4}
            rootProps={{ insetBlockStart: '-50px !important' }}
            zIndex="dropdown"
            minW="unset"
            borderRadius="22px"
            borderTop="none"
          >
            {menuItems[profile ? 'loggedInMenuItems' : 'notLoggedInMenuItems']
              .filter((item) => !item.hide)
              .map((item, index) => (
                <React.Fragment key={index}>
                  <MenuItem
                    closeOnSelect={!item.onClick}
                    display="flex"
                    justifyContent="center"
                    p={0}
                    as={item.href ? Link : 'div'}
                    href={item.href}
                    onClick={item.onClick}
                  >
                    {item.rightElement}
                    <Text paddingBlock={2.5} fontSize="sm" textAlign="center">
                      {item.title}
                    </Text>
                  </MenuItem>
                  <MenuDivider
                    borderColor={'secondary.100'}
                    _last={{ display: 'none' }}
                    w="100%"
                  />
                </React.Fragment>
              ))}

            {isAuthenticated && (
              <>
                <LogoutConfirmModal isOpen={isOpen} onClose={onClose} />
              </>
            )}
          </MenuList>
        </Menu>
      </Box>
    </Box>
  );
};
export default HeaderProfile;
